'use client';

import React from 'react';
import { clampExcerpt } from '@/helpers/excerpt';
import {
  StyledContentWrapper,
  StyledSectionLabel,
  StyledSectionHeading,
  StyledSectionIntro,
  StyledSection,
  StyledInner,
  StyledStepsGrid,
  StyledStep,
  StyledFormulaBox,
  StyledFormulaBody,
  StyledDataTable,
  StyledBadge,
  StyledTableFootnote,
  StyledFactorsGrid,
  StyledFactorCard,
  StyledTipsGrid,
  StyledTipCard,
  StyledMetricsTable,
  StyledRelatedGrid,
  StyledRelatedCard,
} from './HeadingStructureContent.styles';

interface IRelatedPost {
  _id?: string;
  name: string;
  slug: string;
  excerpt: string;
}

interface IProps {
  relatedPosts: IRelatedPost[];
}

const steps = [
  {
    number: '1',
    title: 'Paste your HTML or markdown',
    description:
      'Copy your full page HTML or markdown text and paste it into the input area. The tool works with both raw HTML using h1-h6 tags and markdown using # through ###### heading syntax.',
  },
  {
    number: '2',
    title: 'Click Analyze',
    description:
      'The tool instantly extracts all headings, builds your heading tree, and checks for four common issues: missing H1, multiple H1 tags, skipped heading levels, and empty headings. No sign-up required.',
  },
  {
    number: '3',
    title: 'Review and fix your structure',
    description:
      'See a visual tree of your heading hierarchy with any issues highlighted. Each issue includes a clear description of what is wrong and how to fix it. Status is Good (zero issues), Fair (1-2 issues), or Poor (3 or more issues).',
  },
];

const headingRules = [
  { rule: 'One H1 per page', importance: 'Critical', description: 'Your H1 is the top-level topic signal. Multiple H1s confuse crawlers. Missing H1 wastes your most important heading tag.' },
  { rule: 'Sequential hierarchy', importance: 'High', description: 'Never skip levels. H1 to H3 without H2 breaks the document outline. Maintain H1 > H2 > H3 order.' },
  { rule: 'Keyword in H1', importance: 'High', description: 'Your primary target keyword should appear in the H1. This is one of the strongest on-page ranking signals.' },
  { rule: 'Keywords in H2 headings', importance: 'Medium', description: 'Place secondary keywords and related terms in H2 headings. This expands topical coverage and helps rankings for related queries.' },
  { rule: 'No empty headings', importance: 'Medium', description: 'Empty heading tags are invisible to users but confuse screen readers and add junk signals to crawlers.' },
  { rule: 'Descriptive heading text', importance: 'Medium', description: 'Headings like "Section 1" add no SEO value. Write headings that describe the content and include relevant terms.' },
];

const headingByContentType = [
  { type: 'Blog post', h1: '1', h2: '3-6', h3: '2-4 per H2', notes: 'H1 matches the post title. H2 defines each main section. H3 breaks down subsections within each H2.' },
  { type: 'Product page', h1: '1', h2: '2-4', h3: '1-3 per H2', notes: 'H1 is the product name. H2 covers features, specs, reviews. H3 organizes detail within each section.' },
  { type: 'Landing page', h1: '1', h2: '3-5', h3: 'Optional', notes: 'H1 states the value proposition. H2 covers each benefit or feature. H3 adds supporting detail.' },
  { type: 'FAQ page', h1: '1', h2: '1-3', h3: 'One per question', notes: 'H1 names the FAQ topic. H2 groups questions by theme. H3 is each individual question.' },
  { type: 'Category page', h1: '1', h2: '1-2', h3: 'Optional', notes: 'H1 is the category name. H2 introduces subcategories or filters. Avoid over-structuring short pages.' },
  { type: 'Pillar content', h1: '1', h2: '6-12', h3: '3-6 per H2', notes: 'Long-form content requires deep hierarchy. H2 covers major subtopics. H3 and H4 add granular sections.' },
];

const headingMistakes = [
  {
    icon: '🔴',
    title: 'Missing H1 tag',
    description:
      'A page without an H1 is like a document without a title. Search engines use the H1 to understand what the entire page is about. Without it, you are leaving the most important heading signal completely unused. Every page needs exactly one H1.',
    stat: 'H1 is the strongest single on-page keyword signal',
  },
  {
    icon: '🔁',
    title: 'Multiple H1 tags',
    description:
      'Using two or more H1 tags dilutes your topical focus and confuses search engine crawlers about which heading defines the page. This often happens when page templates apply H1 styling to multiple sections for visual reasons. Use a single H1 and style other headings with CSS instead.',
    stat: 'One H1 per page, always',
  },
  {
    icon: '⏭️',
    title: 'Skipping heading levels',
    description:
      'Jumping from H1 directly to H3, or H2 to H4, breaks the document outline that both search engines and accessibility tools rely on. Screen readers navigate pages by heading level. Search engines use heading hierarchy to understand content organization. Always move one level at a time.',
    stat: 'Maintain sequential H1 > H2 > H3 order',
  },
  {
    icon: '📭',
    title: 'Generic or keyword-free headings',
    description:
      'Headings like "Introduction," "Overview," or "Section 2" are visible to crawlers but contribute nothing to keyword relevance. Every heading is an opportunity to include your target keyword or a related term. Rewrite generic headings to be descriptive and topically relevant.',
    stat: 'Every heading should include a keyword or related term',
  },
  {
    icon: '🎨',
    title: 'Using headings for visual styling only',
    description:
      'Applying H1 or H2 tags purely for font size or visual impact, rather than to define content hierarchy, corrupts your page outline. Use CSS classes and styled components to control font size and weight. Reserve heading tags for actual content structure and hierarchy.',
    stat: 'Never use heading tags as styling shortcuts',
  },
  {
    icon: '📋',
    title: 'Duplicating H1 in the page title tag',
    description:
      'Your H1 and your meta title tag can be similar but do not need to be identical. The meta title is optimized for search result click-through. The H1 is optimized for on-page reading experience. A slight variation lets you target different keyword phrasings without duplication issues.',
    stat: 'H1 and meta title can differ slightly for dual optimization',
  },
];

const tips = [
  {
    number: '01',
    title: 'Write your H1 around your primary keyword',
    description:
      'Your H1 should include your exact target keyword in a natural, readable sentence or phrase. Search engines weight the H1 heavily for topical relevance. Place the keyword near the beginning of the H1 for maximum impact. Avoid stuffing multiple keywords into a single H1.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '02',
    title: 'Map your content outline before writing',
    description:
      'Plan your heading hierarchy before you write. Sketch out H1, H2, and H3 levels with target keywords before drafting content. This prevents structural problems and ensures every section has a clear purpose. A pre-planned outline produces cleaner heading hierarchies than headings added during editing.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '03',
    title: 'Add keyword-rich FAQ sections with accordions',
    description:
      'FAQ accordions naturally use H3 headings for each question. Each question becomes a heading that search engines index, expanding your keyword coverage beyond your main headings. Accordion-organized FAQs also qualify for FAQ schema markup, which can generate rich results in Google.',
    cta: 'Try Accordion widget',
    ctaHref: '/widgets/accordion',
  },
  {
    number: '04',
    title: 'Organize multi-topic pages with tab headings',
    description:
      'Tab widgets let you present separate content blocks, each with its own subheadings, under a single URL. This keeps your page heading hierarchy clean while expanding the depth of structured content available to both readers and search engines.',
    cta: 'Try Tabs widget',
    ctaHref: '/widgets/tabs',
  },
  {
    number: '05',
    title: 'Use comparison table headers for feature keywords',
    description:
      'HTML table headers are semantically meaningful to search engines. Comparison table widgets create structured, header-rich content that reinforces your page keywords in a format that also earns featured snippets for product comparison queries.',
    cta: 'Try Comparison Tables widget',
    ctaHref: '/widgets/comparison-tables',
  },
  {
    number: '06',
    title: 'Build fresh heading structure with content feeds',
    description:
      'Dynamic content feed widgets add new H2 and H3-level content to your pages on a regular schedule. New heading-organized content gives search engines a reason to crawl your page more frequently and expands the keyword signals your page sends over time.',
    cta: 'Try Feeds widget',
    ctaHref: '/widgets/feeds',
  },
  {
    number: '07',
    title: 'Test your heading structure after every major edit',
    description:
      'Content migrations, design changes, and CMS updates often introduce heading regressions. An H1 can accidentally be deleted or duplicated during template changes. Run this free heading analyzer after every significant content edit or template update to catch issues before they affect rankings.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '08',
    title: 'Audit heading structure across your entire site',
    description:
      'Single-page heading checks are useful, but site-wide audits reveal patterns. Use Screaming Frog or Google Search Console to identify pages with missing H1s or multiple H1s across your whole site. Fix the highest-traffic pages first for the fastest SEO impact.',
    cta: null,
    ctaHref: null,
  },
];

const glossaryMetrics = [
  {
    term: 'Document Outline',
    definition: 'The hierarchical structure of a web page formed by its heading tags. A correct document outline flows from H1 to H2 to H3 without skipping levels, creating a readable table of contents for both bots and screen readers.',
    formula: 'H1 > H2 > H3 > H4 > H5 > H6',
    whenToUse: 'Evaluating the structural integrity of any web page',
  },
  {
    term: 'Semantic HTML',
    definition: 'HTML that uses tags according to their meaning, not just their visual appearance. Using H1-H6 for content hierarchy (not font sizing) is a core principle of semantic HTML. Search engines score pages higher for correct semantic structure.',
    formula: 'Correct tag usage for meaning, not style',
    whenToUse: 'Writing or auditing any HTML page for SEO and accessibility',
  },
  {
    term: 'Heading Prominence',
    definition: 'A measure of how early and how often a keyword appears in headings relative to the rest of the page. Keywords in H1 have the highest prominence. Keywords in H2-H3 have secondary prominence.',
    formula: 'Heading level x keyword position',
    whenToUse: 'Evaluating keyword placement strategy across a page',
  },
  {
    term: 'Accessibility (WCAG)',
    definition: 'Web Content Accessibility Guidelines require proper heading structure so screen readers can navigate pages by heading level. Skipped heading levels and multiple H1 tags violate WCAG 2.4.6, which mandates descriptive and hierarchical headings.',
    formula: 'WCAG 2.4.6 Headings and Labels',
    whenToUse: 'Building accessible pages that also rank well in search',
  },
  {
    term: 'Featured Snippet',
    definition: 'A highlighted search result that appears above organic listings, often pulled from a heading and the content immediately following it. Well-structured H2 and H3 headings that directly answer questions are strong featured snippet candidates.',
    formula: 'Heading + following paragraph = snippet candidate',
    whenToUse: 'Targeting position zero in Google search results',
  },
];

export const HeadingStructureContent = ({ relatedPosts }: IProps) => {
  return (
    <StyledContentWrapper>

      {/* How It Works */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>How It Works</StyledSectionLabel>
          <StyledSectionHeading>How to use this free heading structure analyzer</StyledSectionHeading>
          <StyledSectionIntro>
            No account needed, no sign-up required. Completely free. Paste your HTML
            or markdown and get a full heading hierarchy analysis in seconds.
          </StyledSectionIntro>
          <StyledStepsGrid>
            {steps.map((step) => (
              <StyledStep key={step.number}>
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </StyledStep>
            ))}
          </StyledStepsGrid>
        </StyledInner>
      </StyledSection>

      {/* The Rules */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner maxWidth="720px">
          <StyledSectionLabel>The Rules</StyledSectionLabel>
          <StyledSectionHeading>
            How heading structure analysis works
          </StyledSectionHeading>
          <StyledSectionIntro>
            This free analyzer checks four structural rules on every analysis. Here is
            exactly what is checked and why each rule matters for your SEO.
          </StyledSectionIntro>

          <StyledFormulaBox>
            <p className="label">Issue Scoring</p>
            <p className="formula">0 issues = Good &nbsp;|&nbsp; 1-2 issues = Fair &nbsp;|&nbsp; 3+ issues = Poor</p>
            <p className="example">
              Checks: <span>Missing H1, Multiple H1s, Skipped levels, Empty headings</span>
            </p>
          </StyledFormulaBox>

          <StyledFormulaBody>
            <p>
              The analyzer first extracts all heading tags from your HTML or markdown.
              It builds a sequential list of headings in the order they appear in your
              content, then runs four checks.
            </p>
            <p>
              The missing H1 check counts how many H1 tags are present. Zero H1 tags
              triggers an issue. Multiple H1 tags trigger a separate issue. Either way,
              your page should have exactly one.
            </p>
            <p>
              The skipped levels check walks through your heading list in order and
              compares each heading level to the previous one. If the next heading is
              more than one level deeper, a skipped level issue is flagged with the
              heading text so you can find it quickly.
            </p>
            <p>
              Status is calculated from the total issue count. Zero issues earns Good.
              One or two issues earns Fair. Three or more earns Poor. Fix the issues in
              order of severity: missing H1 first, skipped levels second, empty headings last.
            </p>
          </StyledFormulaBody>
        </StyledInner>
      </StyledSection>

      {/* Rules Table */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Heading SEO Rules</StyledSectionLabel>
          <StyledSectionHeading>
            Six heading rules every SEO-optimized page must follow
          </StyledSectionHeading>
          <StyledSectionIntro>
            Use this reference when writing new content or auditing existing pages for
            heading structure issues.
          </StyledSectionIntro>

          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Importance</th>
                  <th>Why It Matters</th>
                </tr>
              </thead>
              <tbody>
                {headingRules.map((row) => (
                  <tr key={row.rule}>
                    <td className="col-name">{row.rule}</td>
                    <td>
                      <StyledBadge
                        color={row.importance === 'Critical' ? '#991b1b' : row.importance === 'High' ? '#92400e' : '#166534'}
                        bg={row.importance === 'Critical' ? '#fee2e2' : row.importance === 'High' ? '#fef3c7' : '#dcfce7'}
                      >
                        {row.importance}
                      </StyledBadge>
                    </td>
                    <td className="col-note">{row.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>

          <StyledTableFootnote>
            Sources: Google Search Central, Moz On-Page SEO Guide, 2026.
          </StyledTableFootnote>
        </StyledInner>
      </StyledSection>

      {/* Heading by Content Type */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>Heading Structure by Content Type</StyledSectionLabel>
          <StyledSectionHeading>
            Recommended heading patterns for each content format
          </StyledSectionHeading>
          <StyledSectionIntro>
            Different page types call for different heading depths and patterns.
            Use these templates as a baseline for your content outlines.
          </StyledSectionIntro>

          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>Content Type</th>
                  <th>H1</th>
                  <th>H2</th>
                  <th>H3</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {headingByContentType.map((row) => (
                  <tr key={row.type}>
                    <td className="col-name">{row.type}</td>
                    <td className="col-value">{row.h1}</td>
                    <td className="col-value">{row.h2}</td>
                    <td className="col-value">{row.h3}</td>
                    <td className="col-note">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>

          <StyledTableFootnote>
            Benchmarks based on top-ranking pages and SEO content analysis, 2026.
          </StyledTableFootnote>
        </StyledInner>
      </StyledSection>

      {/* 6 Mistakes */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>What Kills Your Heading Strategy</StyledSectionLabel>
          <StyledSectionHeading>
            Six heading mistakes that hurt your SEO and accessibility
          </StyledSectionHeading>
          <StyledSectionIntro>
            These mistakes appear on millions of pages. Most are easy to fix once you
            know what to look for.
          </StyledSectionIntro>

          <StyledFactorsGrid>
            {headingMistakes.map((factor) => (
              <StyledFactorCard key={factor.title}>
                <span className="factor-icon">{factor.icon}</span>
                <h3>{factor.title}</h3>
                <p>{factor.description}</p>
                <span className="factor-stat">{factor.stat}</span>
              </StyledFactorCard>
            ))}
          </StyledFactorsGrid>
        </StyledInner>
      </StyledSection>

      {/* 8 Tips */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>Optimize Your Heading Structure</StyledSectionLabel>
          <StyledSectionHeading>
            8 tips to build SEO-optimized heading hierarchies
          </StyledSectionHeading>
          <StyledSectionIntro>
            These strategies help you use headings to drive both search visibility and
            reader engagement. All CommonNinja widgets are free to start.
          </StyledSectionIntro>

          <StyledTipsGrid>
            {tips.map((tip) => (
              <StyledTipCard key={tip.number}>
                <div className="tip-header">
                  <div className="tip-number">{tip.number}</div>
                  <h3>{tip.title}</h3>
                </div>
                <p>{tip.description}</p>
                {tip.cta && tip.ctaHref && (
                  <a className="tip-cta" href={tip.ctaHref} target="_blank" rel="noopener noreferrer">
                    {tip.cta} →
                  </a>
                )}
              </StyledTipCard>
            ))}
          </StyledTipsGrid>
        </StyledInner>
      </StyledSection>

      {/* Glossary */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>SEO and Accessibility Glossary</StyledSectionLabel>
          <StyledSectionHeading>
            Key heading and document structure terms explained
          </StyledSectionHeading>
          <StyledSectionIntro>
            Heading structure connects SEO, accessibility, and semantic HTML. Here is
            how the key concepts relate and when each matters most.
          </StyledSectionIntro>

          <StyledMetricsTable>
            <table>
              <thead>
                <tr>
                  <th>Term</th>
                  <th>Definition</th>
                  <th>Format</th>
                  <th>When to Use</th>
                </tr>
              </thead>
              <tbody>
                {glossaryMetrics.map((row) => (
                  <tr key={row.term}>
                    <td className="metric-name">{row.term}</td>
                    <td>{row.definition}</td>
                    <td><span className="metric-formula">{row.formula}</span></td>
                    <td>{row.whenToUse}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledMetricsTable>
        </StyledInner>
      </StyledSection>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <StyledSection bg="var(--gray-50)">
          <StyledInner>
            <StyledSectionLabel>From the Blog</StyledSectionLabel>
            <StyledSectionHeading>Further reading on heading structure and on-page SEO</StyledSectionHeading>
            <StyledSectionIntro>
              Dig deeper into heading optimization, document structure,
              and on-page SEO strategies that drive organic traffic.
            </StyledSectionIntro>
            <StyledRelatedGrid>
              {relatedPosts.map((post) => (
                <StyledRelatedCard
                  key={post.slug}
                  href={`/blog/${post.slug}`}
                >
                  <h3>{post.name}</h3>
                  {post.excerpt && (
                    <p dangerouslySetInnerHTML={{ __html: clampExcerpt(post.excerpt) }} />
                  )}
                  <span className="read-more">Read article →</span>
                </StyledRelatedCard>
              ))}
            </StyledRelatedGrid>
          </StyledInner>
        </StyledSection>
      )}

    </StyledContentWrapper>
  );
};
